"use client";

import { type ChangeEvent, useCallback, useId, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { DESTINATIONS_ARRAY } from "@/constants/gallery-images";
import { useMainGalleryQueries } from "@/hooks/use-main-gallery-queries";
import type { Destinations } from "@/types/images-categories.types";

const DESTINATION_LABELS: Record<Destinations, string> = {
  all: "Svi zahvati",
  veneers: "Ljuskice",
  whitening: "Izbjeljivanje zubi",
  implants: "Zubni implantati",
  crowns: "Zubne krunice",
  braces: "Ortodonski aparatić",
  invisalign: "Invisalign",
};

export function MainGalleryDestinations() {
  const { mainGallery, setMainGallery } = useMainGalleryQueries();
  const [isOpen, setIsOpen] = useState(false);
  const selectId = useId();

  const options = useMemo(
    () =>
      DESTINATIONS_ARRAY.map((destination) => ({
        value: destination,
        label: DESTINATION_LABELS[destination as Destinations],
      })),
    [],
  );

  const handleSelectOptionChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const destination = event.target.value as Destinations;

      setMainGallery((prev) => ({
        filter: prev.filter,
        destination,
      }));
    },
    [setMainGallery],
  );

  const { destination: selectedDestination } = mainGallery;

  return (
    <div className="flex w-full flex-col gap-3 lg:gap-6 lg:w-fit lg:flex-row lg:items-center">
      <label
        className="text-sm tracking-[-0.02em] leading-[110%] font-medium"
        htmlFor={selectId}
      >
        Zahvati
      </label>
      <div className="relative w-full lg:min-w-96 lg:max-w-96">
        <select
          className={cn(
            "box-border w-full appearance-none",
            "border border-brand-800/15",
            "px-8 py-2.5 lg:py-[15px]",
            "text-sm tracking-[-0.02em] leading-[110%] font-medium text-brand-900",
          )}
          id={selectId}
          onBlur={() => setIsOpen(false)}
          onChange={handleSelectOptionChange}
          onFocus={() => setIsOpen(true)}
          value={selectedDestination}
        >
          {options.map((option) => (
            <option
              className="text-sm tracking-[-0.02em] leading-[110%] font-medium text-brand-900"
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-8 flex items-center">
          <svg
            className={cn(
              "h-1.5 w-3 transition-all duration-300",
              isOpen && "rotate-180",
            )}
            fill="currentColor"
            viewBox="0 0 12 6"
          >
            <path d="M12 0L6 6L0 0H12Z" />
          </svg>
        </div>
      </div>
    </div>
  );
}
